import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Smartphone, Clock, Zap, CheckCircle2, AlertTriangle, Send,
  RefreshCw, QrCode, Settings, History, ChevronRight, Info, Activity
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';

const fmtMoeda = (v) => `R$ ${(v || 0).toFixed(2).replace('.', ',')}`;

// ─── Hook que retorna countdown HH:MM até a próxima execução ─────────────────
function useCountdown(horarios, ativo) {
  const [t, setT] = useState('—');
  useEffect(() => {
    if (!ativo || !horarios?.length) { setT('—'); return; }
    const calc = () => {
      const now = new Date();
      const horariosOrdenados = [...horarios].sort();
      let proxima = null;
      for (const h of horariosOrdenados) {
        const [hh, mm] = h.split(':').map(Number);
        const cand = new Date(now);
        cand.setHours(hh, mm, 0, 0);
        if (cand > now) { proxima = cand; break; }
      }
      if (!proxima) {
        // Próximo é amanhã o primeiro horário
        const [hh, mm] = horariosOrdenados[0].split(':').map(Number);
        proxima = new Date(now);
        proxima.setDate(proxima.getDate() + 1);
        proxima.setHours(hh, mm, 0, 0);
      }
      const diff = Math.max(0, proxima - now);
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setT(`${String(h).padStart(2, '0')}h ${String(m).padStart(2, '0')}m`);
    };
    calc();
    const i = setInterval(calc, 30000);
    return () => clearInterval(i);
  }, [horarios, ativo]);
  return t;
}


export default function CommandCenterHero({ API, config, onToggle, togglingActive, connectionState, onOpenQR, onExecuteNow, executingNow }) {
  const [diag, setDiag] = useState(null);
  const [loadingDiag, setLoadingDiag] = useState(true);

  const carregarDiag = useCallback(async () => {
    setLoadingDiag(true);
    try {
      const res = await axios.get(`${API}/notificacoes/automacao/diagnostico`, { withCredentials: true });
      setDiag(res.data);
    } catch {
      // silent
    } finally {
      setLoadingDiag(false);
    }
  }, [API]);

  useEffect(() => { carregarDiag(); }, [carregarDiag]);

  const horarios = config?.horarios_disparo || (config?.hora_disparo ? [config.hora_disparo] : ['09:00']);
  const countdown = useCountdown(horarios, config?.ativo);
  const stats = diag?.stats || { elegiveis: 0, sem_telefone: 0, regra_desativada: 0, cooldown_ativo: 0, blacklist: 0, clientes_unicos: 0 };

  return (
    <div className="space-y-3" data-testid="command-center-hero">
      {/* HERO STATUS BAR */}
      <Card className={`border-2 ${config?.ativo ? 'border-emerald-300 bg-emerald-50/30' : 'border-slate-300'}`}>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Lado WhatsApp */}
            <div className="flex items-center gap-3">
              {connectionState?.profile_pic ? (
                <img src={connectionState.profile_pic} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-emerald-400 shrink-0"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              ) : (
                <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${connectionState?.status === 'connected' ? 'bg-emerald-100' : 'bg-red-100'}`}>
                  <Smartphone className={`w-6 h-6 ${connectionState?.status === 'connected' ? 'text-emerald-700' : 'text-red-700'}`} />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-bold text-slate-900 truncate text-sm">{connectionState?.profile_name || 'WhatsApp'}</p>
                  {connectionState?.status === 'connected'
                    ? <span className="bg-emerald-100 text-emerald-800 text-[10px] font-bold px-1.5 py-0.5 rounded uppercase">● Online</span>
                    : <span className="bg-red-100 text-red-700 text-[10px] font-bold px-1.5 py-0.5 rounded uppercase">Offline</span>}
                </div>
                <p className="text-xs text-slate-500 font-mono truncate">{connectionState?.numero ? `+${connectionState.numero}` : '—'}</p>
              </div>
              <Button
                onClick={() => onToggle(!config?.ativo)}
                disabled={togglingActive}
                variant="ghost"
                className="p-0 hover:bg-transparent shrink-0"
                data-testid="cc-toggle-automacao"
              >
                <span className={`w-14 h-7 rounded-full relative transition-colors block ${config?.ativo ? 'bg-emerald-600' : 'bg-slate-300'}`}>
                  <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-all ${config?.ativo ? 'left-7' : 'left-0.5'}`} />
                </span>
              </Button>
            </div>

            {/* Lado Próxima Execução */}
            <div className="border-t lg:border-t-0 lg:border-l border-slate-200 pt-3 lg:pt-0 lg:pl-4 flex items-center gap-3">
              <Clock className={`w-5 h-5 shrink-0 ${config?.ativo ? 'text-emerald-600' : 'text-slate-400'}`} />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500">
                  {config?.ativo ? 'Próxima execução' : 'Automação desactivada'}
                </p>
                <p className="text-2xl font-bold text-slate-900 tabular-nums">
                  {config?.ativo ? `em ${countdown}` : 'Pausada'}
                </p>
                <p className="text-[10px] text-slate-500">Horários: {horarios.join(' · ')}</p>
              </div>
              <Button
                onClick={onExecuteNow}
                disabled={executingNow}
                className="bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs uppercase tracking-wide gap-1.5 shrink-0"
                data-testid="cc-disparar-agora"
              >
                {executingNow
                  ? <span className="animate-spin h-3.5 w-3.5 border-2 border-white border-b-transparent rounded-full" />
                  : <Zap className="w-3.5 h-3.5" />}
                Disparar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* BENTO METRICS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {/* Elegíveis */}
        <Card className="border-emerald-200 hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 mb-2" />
            <p className="text-3xl font-bold text-slate-900 tabular-nums" data-testid="metric-elegiveis">
              {loadingDiag ? '...' : stats.elegiveis}
            </p>
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mt-1">Elegíveis agora</p>
            <p className="text-[10px] text-emerald-700 font-medium mt-0.5">Pronto para envio</p>
          </CardContent>
        </Card>

        {/* Sem telefone */}
        <Card className="border-amber-200 hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <AlertTriangle className="w-5 h-5 text-amber-600 mb-2" />
            <p className="text-3xl font-bold text-slate-900 tabular-nums" data-testid="metric-sem-telefone">
              {loadingDiag ? '...' : stats.sem_telefone}
            </p>
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mt-1">Sem telefone</p>
            {stats.sem_telefone > 0 && (
              <p className="text-[10px] text-amber-700 font-medium mt-0.5">Precisam cadastro</p>
            )}
          </CardContent>
        </Card>

        {/* Em cooldown */}
        <Card className="border-blue-200 hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <Clock className="w-5 h-5 text-blue-600 mb-2" />
            <p className="text-3xl font-bold text-slate-900 tabular-nums" data-testid="metric-cooldown">
              {loadingDiag ? '...' : stats.cooldown_ativo}
            </p>
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mt-1">Em cooldown</p>
            <p className="text-[10px] text-blue-700 font-medium mt-0.5">Notificadas há pouco</p>
          </CardContent>
        </Card>

        {/* Total clientes */}
        <Card className="border-slate-200 hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <Activity className="w-5 h-5 text-slate-600 mb-2" />
            <p className="text-3xl font-bold text-slate-900 tabular-nums" data-testid="metric-clientes-unicos">
              {loadingDiag ? '...' : stats.clientes_unicos}
            </p>
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mt-1">Clientes vencidos</p>
            <p className="text-[10px] text-slate-500 mt-0.5">Total agora</p>
          </CardContent>
        </Card>
      </div>

      {/* AÇÕES AUXILIARES */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="outline" size="sm" onClick={onOpenQR} className="gap-1.5" data-testid="cc-btn-qr">
          <QrCode className="w-3.5 h-3.5" /> Gerenciar conexão
        </Button>
        <Button variant="outline" size="sm" onClick={carregarDiag} disabled={loadingDiag} className="gap-1.5" data-testid="cc-btn-refresh-diag">
          <RefreshCw className={`w-3.5 h-3.5 ${loadingDiag ? 'animate-spin' : ''}`} /> Atualizar diagnóstico
        </Button>
        {connectionState?.numero && (
          <span className="text-xs text-slate-500 ml-auto">
            Instância: <span className="font-mono font-semibold">{connectionState.instancia}</span>
          </span>
        )}
      </div>
    </div>
  );
}
